import { useState } from "react";
import {
  Plus,
  Trash2,
  Users,
  Pencil,
  Video,
  AudioLines,
  Signal,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  User,
  Download,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import {
  useStreamTakers,
  useStreamTakerMutations,
  type StreamTaker,
  type StreamTakerContact,
} from "@/hooks/useStreamTakers";
import { useStreamSignals } from "@/hooks/useStreamSignals";
import { usePeople } from "@/hooks/usePeople";
import { downloadPersonVCard } from "@/lib/vcard";
import type { StreamSignal } from "@/hooks/useStreamSignals";

interface StreamTakerListProps {
  projectId: string;
}

interface TakerFormData {
  name: string;
  deliveryMethod: string;
  url: string;
  signalID: string | null;
  notes: string;
  testDate: string;
}

const emptyForm: TakerFormData = {
  name: "",
  deliveryMethod: "",
  url: "",
  signalID: null,
  notes: "",
  testDate: "",
};

function toDateTimeLocal(ts: number | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateTime(ts: number | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateTimeLocal(s: string): number | null {
  if (!s) return null;
  return new Date(s).getTime();
}

function audioPairLabel(index: number) {
  return `${index * 2 + 1}+${index * 2 + 2}`;
}

function isAllPreTestDone(taker: StreamTaker): boolean {
  const audioFeedLinks = taker.signal?.audioFeedLinks ?? [];
  if (audioFeedLinks.length === 0) return taker.preTestDone;
  const tests = taker.audioPairTests ?? [];
  return (
    taker.preTestDone &&
    audioFeedLinks.every((link) => {
      const t = tests.find((test) => test.signalAudioFeedID === link.id);
      return t?.preTestDone === true;
    })
  );
}

function isAllOnDayTestDone(taker: StreamTaker): boolean {
  const audioFeedLinks = taker.signal?.audioFeedLinks ?? [];
  if (audioFeedLinks.length === 0) return taker.onDayTestDone;
  const tests = taker.audioPairTests ?? [];
  return (
    taker.onDayTestDone &&
    audioFeedLinks.every((link) => {
      const t = tests.find((test) => test.signalAudioFeedID === link.id);
      return t?.onDayTestDone === true;
    })
  );
}

export const StreamTakerList = ({ projectId }: StreamTakerListProps) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTaker, setEditingTaker] = useState<StreamTaker | null>(null);
  const [deleteTakerId, setDeleteTakerId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: takers = [], isLoading } = useStreamTakers(projectId);
  const { data: signals = [] } = useStreamSignals(projectId);
  const mutations = useStreamTakerMutations();

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleOpenCreate = () => {
    setEditingTaker(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (taker: StreamTaker) => {
    setEditingTaker(taker);
    setIsFormOpen(true);
  };

  const handleSubmit = (data: TakerFormData) => {
    const payload = {
      name: data.name,
      deliveryMethod: data.deliveryMethod,
      url: data.url,
      signalID: data.signalID,
      notes: data.notes,
      testDate: fromDateTimeLocal(data.testDate),
    };
    if (editingTaker) {
      mutations.updateTaker({
        id: editingTaker.id,
        projectID: projectId,
        ...payload,
      });
      setIsFormOpen(false);
    } else {
      void mutations.createTaker(
        { projectID: projectId, ...payload },
        { onSuccess: () => setIsFormOpen(false) },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTakerId) return;
    void mutations.deleteTaker(
      { id: deleteTakerId, projectId },
      { onSuccess: () => setDeleteTakerId(null) },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Users className="h-5 w-5" /> Stream Takers
        </h3>
        <Button className="w-full sm:w-auto" onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Stream Taker
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading stream takers...
          </CardContent>
        </Card>
      ) : takers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Users className="h-8 w-8 mb-2" />
            <p>No stream takers configured</p>
            <p className="text-sm">
              Add stream takers to track who receives your streams
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="hidden overflow-hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Delivery</th>
                    <th className="px-3 py-2 font-medium">Signal</th>
                    <th className="px-3 py-2 font-medium">URL</th>
                    <th className="px-3 py-2 font-medium">Test Date</th>
                    <th className="px-3 py-2 font-medium text-center w-20">
                      Pre-Test
                    </th>
                    <th className="px-3 py-2 font-medium text-center w-20">
                      On Day
                    </th>
                    <th className="px-3 py-2 w-20" />
                  </tr>
                </thead>
                <tbody>
                  {takers.map((taker) => (
                    <StreamTakerRow
                      key={taker.id}
                      taker={taker}
                      projectId={projectId}
                      mutations={mutations}
                      isExpanded={expandedIds.has(taker.id)}
                      onToggleExpand={() => toggleExpanded(taker.id)}
                      onEdit={() => handleOpenEdit(taker)}
                      onDelete={() => setDeleteTakerId(taker.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="space-y-3 md:hidden">
            {takers.map((taker) => (
              <StreamTakerMobileCard
                key={taker.id}
                taker={taker}
                projectId={projectId}
                mutations={mutations}
                isExpanded={expandedIds.has(taker.id)}
                onToggleExpand={() => toggleExpanded(taker.id)}
                onEdit={() => handleOpenEdit(taker)}
                onDelete={() => setDeleteTakerId(taker.id)}
              />
            ))}
          </div>
        </>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTaker ? "Edit Stream Taker" : "Add Stream Taker"}
            </DialogTitle>
          </DialogHeader>
          <StreamTakerForm
            initialData={editingTaker ?? undefined}
            signals={signals}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormOpen(false)}
            isLoading={false}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTakerId}
        onOpenChange={(open) => !open && setDeleteTakerId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Stream Taker?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this stream taker and all associated
              data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============ Stream Taker Row ============

type StreamTakerMutations = ReturnType<typeof useStreamTakerMutations>;

const StreamTakerMobileCard = ({
  taker,
  projectId,
  mutations,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
}: {
  taker: StreamTaker;
  projectId: string;
  mutations: StreamTakerMutations;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const signal = taker.signal;
  const audioFeedLinks = signal?.audioFeedLinks ?? [];

  const getAudioPairTestState = (signalAudioFeedID: string) => {
    return (taker.audioPairTests ?? []).find(
      (t) => t.signalAudioFeedID === signalAudioFeedID,
    );
  };

  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start gap-2">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
            onClick={onToggleExpand}
          >
            <span className="pt-0.5">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{taker.name}</span>
              <span className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span className="min-w-0 max-w-full truncate">
                  {taker.url || "—"}
                </span>
                <span className="whitespace-nowrap">
                  {formatDateTime(taker.testDate)}
                </span>
                {signal && (
                  <Badge
                    variant="secondary"
                    className="max-w-full gap-1 truncate text-xs font-normal"
                  >
                    <Signal className="h-3 w-3 shrink-0" />
                    <span className="truncate">{signal.name}</span>
                  </Badge>
                )}
              </span>
            </span>
          </button>

          <span className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </span>
        </div>

        {isExpanded && (
          <div className="space-y-4 border-t pt-3">
            {signal ? (
              <div className="space-y-1">
                <div className="grid grid-cols-[1fr_72px_72px] gap-2 px-3 text-xs font-medium text-muted-foreground">
                  <span>Feed</span>
                  <span className="text-center">Pre-Test</span>
                  <span className="text-center">On Day</span>
                </div>
                {signal.videoFeed && (
                  <div className="grid grid-cols-[1fr_72px_72px] items-center gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Video className="h-3.5 w-3.5" />
                        <span>Video</span>
                      </span>
                      <span className="truncate">{signal.videoFeed.name}</span>
                    </span>
                    <span className="text-center">
                      <Checkbox
                        checked={taker.preTestDone}
                        onCheckedChange={() =>
                          mutations.updateTaker({
                            id: taker.id,
                            projectID: projectId,
                            preTestDone: !taker.preTestDone,
                          })
                        }
                      />
                    </span>
                    <span className="text-center">
                      <Checkbox
                        checked={taker.onDayTestDone}
                        onCheckedChange={() =>
                          mutations.updateTaker({
                            id: taker.id,
                            projectID: projectId,
                            onDayTestDone: !taker.onDayTestDone,
                          })
                        }
                      />
                    </span>
                  </div>
                )}
                {audioFeedLinks.map((link, linkIndex) => {
                  const testState = getAudioPairTestState(link.id);
                  return (
                    <div
                      key={link.id}
                      className="grid grid-cols-[1fr_72px_72px] items-center gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <AudioLines className="h-3.5 w-3.5" />
                          <Badge
                            variant="outline"
                            className="text-xs font-mono font-normal"
                          >
                            {audioPairLabel(linkIndex)}
                          </Badge>
                        </span>
                        <span className="truncate">
                          {link.audioFeed?.name ?? "Unknown"}
                        </span>
                      </span>
                      <span className="text-center">
                        <Checkbox
                          checked={testState?.preTestDone ?? false}
                          onCheckedChange={() =>
                            mutations.toggleAudioPairTest(
                              taker,
                              link.id,
                              "preTestDone",
                            )
                          }
                        />
                      </span>
                      <span className="text-center">
                        <Checkbox
                          checked={testState?.onDayTestDone ?? false}
                          onCheckedChange={() =>
                            mutations.toggleAudioPairTest(
                              taker,
                              link.id,
                              "onDayTestDone",
                            )
                          }
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No signal assigned
              </p>
            )}

            <TakerContactsSection taker={taker} mutations={mutations} />

            {taker.notes && (
              <div className="text-sm text-muted-foreground">
                <span className="text-xs font-medium">Notes:</span>{" "}
                {taker.notes}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StreamTakerRow = ({
  taker,
  projectId,
  mutations,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
}: {
  taker: StreamTaker;
  projectId: string;
  mutations: StreamTakerMutations;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const signal = taker.signal;
  const audioFeedLinks = signal?.audioFeedLinks ?? [];

  const getAudioPairTestState = (signalAudioFeedID: string) => {
    return (taker.audioPairTests ?? []).find(
      (t) => t.signalAudioFeedID === signalAudioFeedID,
    );
  };

  const allPreTest = isAllPreTestDone(taker);
  const allOnDay = isAllOnDayTestDone(taker);

  return (
    <>
      <tr
        className="border-b hover:bg-muted/50 cursor-pointer"
        onClick={onToggleExpand}
      >
        <td className="px-3 py-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-3 py-2 font-medium">{taker.name}</td>
        <td className="px-3 py-2 text-muted-foreground">
          {taker.deliveryMethod || "—"}
        </td>
        <td className="px-3 py-2">
          {signal ? (
            <Badge variant="secondary" className="text-xs gap-1 font-normal">
              <Signal className="h-3 w-3" /> {signal.name}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate">
          {taker.url || "—"}
        </td>
        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
          {formatDateTime(taker.testDate)}
        </td>
        <td
          className="px-3 py-2 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox checked={allPreTest} disabled className="opacity-100" />
        </td>
        <td
          className="px-3 py-2 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox checked={allOnDay} disabled className="opacity-100" />
        </td>
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>

      {isExpanded && signal && (
        <>
          <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
            <td className="px-3 py-1.5" />
            <td colSpan={5} className="px-3 py-1.5 font-medium">
              Feed
            </td>
            <td className="px-3 py-1.5 font-medium text-center w-20">
              Pre-Test
            </td>
            <td className="px-3 py-1.5 font-medium text-center w-20">On Day</td>
            <td className="px-3 py-1.5" />
          </tr>
          {signal.videoFeed && (
            <tr className="border-b bg-muted/30">
              <td className="px-3 py-1.5" />
              <td colSpan={5} className="px-3 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-muted-foreground min-w-16">
                    <Video className="h-3.5 w-3.5" />
                    <span>Video</span>
                  </span>
                  <span>{signal.videoFeed.name}</span>
                </div>
              </td>
              <td className="px-3 py-1.5 text-center w-20">
                <Checkbox
                  checked={taker.preTestDone}
                  onCheckedChange={() =>
                    mutations.updateTaker({
                      id: taker.id,
                      projectID: projectId,
                      preTestDone: !taker.preTestDone,
                    })
                  }
                />
              </td>
              <td className="px-3 py-1.5 text-center w-20">
                <Checkbox
                  checked={taker.onDayTestDone}
                  onCheckedChange={() =>
                    mutations.updateTaker({
                      id: taker.id,
                      projectID: projectId,
                      onDayTestDone: !taker.onDayTestDone,
                    })
                  }
                />
              </td>
              <td className="px-3 py-1.5" />
            </tr>
          )}
          {audioFeedLinks.map((link, linkIndex) => {
            const testState = getAudioPairTestState(link.id);
            return (
              <tr key={link.id} className="border-b bg-muted/30">
                <td className="px-3 py-1.5" />
                <td colSpan={5} className="px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 text-muted-foreground min-w-16">
                      <AudioLines className="h-3.5 w-3.5" />
                      <Badge
                        variant="outline"
                        className="text-xs font-mono font-normal"
                      >
                        {audioPairLabel(linkIndex)}
                      </Badge>
                    </span>
                    <span>{link.audioFeed?.name ?? "Unknown"}</span>
                  </div>
                </td>
                <td className="px-3 py-1.5 text-center w-20">
                  <Checkbox
                    checked={testState?.preTestDone ?? false}
                    onCheckedChange={() =>
                      mutations.toggleAudioPairTest(
                        taker,
                        link.id,
                        "preTestDone",
                      )
                    }
                  />
                </td>
                <td className="px-3 py-1.5 text-center w-20">
                  <Checkbox
                    checked={testState?.onDayTestDone ?? false}
                    onCheckedChange={() =>
                      mutations.toggleAudioPairTest(
                        taker,
                        link.id,
                        "onDayTestDone",
                      )
                    }
                  />
                </td>
                <td className="px-3 py-1.5" />
              </tr>
            );
          })}
        </>
      )}

      {isExpanded && (
        <tr>
          <td colSpan={9} className="px-3 py-3 bg-muted/30">
            <div className="space-y-4">
              {!signal && (
                <p className="text-xs text-muted-foreground">
                  No signal assigned
                </p>
              )}

              {/* Contacts */}
              <TakerContactsSection taker={taker} mutations={mutations} />

              {/* Notes */}
              {taker.notes && (
                <div className="text-sm text-muted-foreground">
                  <span className="text-xs font-medium">Notes:</span>{" "}
                  {taker.notes}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ============ Taker Contacts Section ============

const TakerContactsSection = ({
  taker,
  mutations,
}: {
  taker: StreamTaker;
  mutations: StreamTakerMutations;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [newPersonId, setNewPersonId] = useState<string | null>(null);
  const [newDescription, setNewDescription] = useState("");

  const { data: allPeople } = usePeople(["contact", "freelancer", "employee"]);

  const contacts = taker.contacts ?? [];

  const peopleOptions =
    allPeople?.map((p) => ({
      value: p.id,
      label: `${p.first_name} ${p.last_name}`,
      subtitle:
        [
          p.person_type === "freelancer" ? "Freelancer" : undefined,
          p.companies?.name,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
    })) ?? [];

  const handleAdd = async () => {
    if (!newPersonId) return;

    setIsCreatingContact(true);
    try {
      await mutations.createContact(
        {
          streamTakerID: taker.id,
          personID: newPersonId,
          description: newDescription.trim(),
        },
        {
          onSuccess: () => {
            setNewPersonId(null);
            setNewDescription("");
            setIsAdding(false);
          },
        },
      );
    } finally {
      setIsCreatingContact(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <User className="h-3 w-3" /> Contacts
        </span>
        {!isAdding && (
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        )}
      </div>

      {contacts.length === 0 && !isAdding && (
        <p className="text-xs text-muted-foreground pl-4">
          No contacts assigned
        </p>
      )}

      {contacts.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="pb-1.5 font-medium">Role</th>
                <th className="pb-1.5 font-medium">Name</th>
                <th className="pb-1.5 font-medium">Phone</th>
                <th className="pb-1.5 font-medium text-right w-20" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) =>
                editingContactId === contact.id ? (
                  <tr key={contact.id}>
                    <td colSpan={4} className="py-1.5">
                      <ContactEditRow
                        contact={contact}
                        taker={taker}
                        peopleOptions={peopleOptions}
                        onSave={(personID, description) => {
                          mutations.updateContact({
                            id: contact.id,
                            streamTakerID: taker.id,
                            personID,
                            description,
                          });
                          setEditingContactId(null);
                        }}
                        onCancel={() => setEditingContactId(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={contact.id} className="border-b last:border-0">
                    <td className="py-1.5 text-muted-foreground">
                      {contact.description || "—"}
                    </td>
                    <td className="py-1.5">
                      {contact.person
                        ? `${contact.person.firstName} ${contact.person.lastName}`
                        : "Unknown"}
                    </td>
                    <td className="py-1.5">
                      {contact.person?.phone ? (
                        <a
                          href={`tel:${contact.person.phone}`}
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {contact.person.phone}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {contact.person &&
                          (contact.person.phone || contact.person.email) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground"
                              title="Download vCard"
                              onClick={() =>
                                downloadPersonVCard({
                                  firstName: contact.person!.firstName,
                                  lastName: contact.person!.lastName,
                                  phone: contact.person!.phone,
                                  email: contact.person!.email,
                                })
                              }
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                          )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          onClick={() => setEditingContactId(contact.id)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            mutations.deleteContact({
                              id: contact.id,
                              streamTakerID: taker.id,
                            })
                          }
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {isAdding && (
        <div className="space-y-2 p-3 rounded-md border bg-muted/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium mb-1 block">Role</label>
              <Input
                placeholder='e.g. "Technical Contact"'
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="h-8 text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Person</label>
              <EntityCombobox
                options={peopleOptions}
                value={newPersonId}
                onChange={setNewPersonId}
                placeholder="Select person"
                searchPlaceholder="Search people..."
                emptyMessage="No people found."
                createLabel="Create new person"
                renderCreateForm={({ onSuccess, onCancel, initialName }) => (
                  <InlinePersonForm
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    initialName={initialName}
                    defaultType="contact"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() => {
                setIsAdding(false);
                setNewPersonId(null);
                setNewDescription("");
              }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              className="h-7"
              onClick={handleAdd}
              disabled={!newPersonId || isCreatingContact}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {isCreatingContact ? "Adding..." : "Add"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ContactEditRow = ({
  contact,
  peopleOptions,
  onSave,
  onCancel,
}: {
  contact: StreamTakerContact;
  taker: StreamTaker;
  peopleOptions: { value: string; label: string; subtitle?: string }[];
  onSave: (personID: string, description: string) => void;
  onCancel: () => void;
}) => {
  const [personID, setPersonID] = useState<string | null>(contact.personID);
  const [description, setDescription] = useState(contact.description);

  return (
    <div className="space-y-2 p-3 rounded-md border bg-muted/30">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium mb-1 block">Role</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Person</label>
          <EntityCombobox
            options={peopleOptions}
            value={personID}
            onChange={setPersonID}
            placeholder="Select person"
            searchPlaceholder="Search people..."
            emptyMessage="No people found."
            createLabel="Create new person"
            renderCreateForm={({ onSuccess, onCancel, initialName }) => (
              <InlinePersonForm
                onSuccess={onSuccess}
                onCancel={onCancel}
                initialName={initialName}
                defaultType="contact"
              />
            )}
          />
        </div>
      </div>
      <div className="flex gap-1 justify-end">
        <Button variant="ghost" size="sm" className="h-7" onClick={onCancel}>
          <X className="h-3.5 w-3.5 mr-1" /> Cancel
        </Button>
        <Button
          size="sm"
          className="h-7"
          onClick={() => personID && onSave(personID, description.trim())}
          disabled={!personID}
        >
          <Check className="h-3.5 w-3.5 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
};

// ============ Stream Taker Form ============

interface StreamTakerFormProps {
  initialData?: StreamTaker;
  signals: StreamSignal[];
  onSubmit: (data: TakerFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const StreamTakerForm = ({
  initialData,
  signals,
  onSubmit,
  onCancel,
  isLoading,
}: StreamTakerFormProps) => {
  const [form, setForm] = useState<TakerFormData>(
    initialData
      ? {
          name: initialData.name,
          deliveryMethod: initialData.deliveryMethod,
          url: initialData.url,
          signalID: initialData.signalID,
          notes: initialData.notes,
          testDate: toDateTimeLocal(initialData.testDate),
        }
      : emptyForm,
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="taker-name">Name *</Label>
        <Input
          id="taker-name"
          placeholder="e.g. SRF, Sky Sports, YouTube"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="taker-delivery">Delivery Method</Label>
        <Input
          id="taker-delivery"
          placeholder="e.g. SRT, RTMP, Fiber, Satellite"
          value={form.deliveryMethod}
          onChange={(e) => setForm({ ...form, deliveryMethod: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="taker-url">URL</Label>
        <Input
          id="taker-url"
          placeholder="e.g. srt://host:port, rtmp://..."
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label>Signal</Label>
        <Select
          value={form.signalID ?? ""}
          onValueChange={(v) => setForm({ ...form, signalID: v || null })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select signal..." />
          </SelectTrigger>
          <SelectContent>
            {signals.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name || "Unnamed signal"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="taker-test-date">Test Date & Time</Label>
        <DateTimePicker
          id="taker-test-date"
          value={form.testDate}
          onChange={(v) => setForm({ ...form, testDate: v })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="taker-notes">Notes</Label>
        <Textarea
          id="taker-notes"
          placeholder="Additional information..."
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={isLoading || !form.name.trim()}>
          {initialData ? "Save Changes" : "Add Stream Taker"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
